import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { showConfirmModal } from "../lib/custom_confirm"
import { setButtonLoading } from "../lib/button_loading"

// Gates a Win Back campaign Send form: submit is intercepted by the styled
// confirm modal, the button goes busy the moment the admin accepts — spinner,
// label unchanged, aria-busy (BUTTON-8) — so a double click never queues twice,
// and while a wave is in flight (server renders the button disabled with its
// reason) the status endpoint is polled so a finished wave
// refreshes the page — re-enabling the button with fresh counters.
export default class extends Controller {
  static targets = ["button"]
  static values = {
    campaign: String,
    statusUrl: String,
    confirmMessage: String,
    active: Boolean,
    pollMs: { type: Number, default: 10000 },
  }

  // Set true right before requestSubmit() re-fires the submit after the user
  // accepts the modal, so confirm() lets that re-entrant submit through.
  #confirmed = false

  connect() {
    if (this.activeValue) this._scheduleNextPoll()
  }

  disconnect() {
    this.stopped = true
    if (this.timer) clearTimeout(this.timer)
  }

  confirm(event) {
    if (this.#confirmed) {
      this.#confirmed = false
      return
    }
    event.preventDefault()
    showConfirmModal(this.confirmMessageValue).then((accepted) => {
      if (!accepted) return
      this.#confirmed = true
      this._disableButton()
      this.element.requestSubmit()
    })
  }

  _disableButton() {
    if (!this.hasButtonTarget) return
    setButtonLoading(this.buttonTarget, true)
  }

  // Self-scheduling poll (never overlaps requests, unlike setInterval).
  _scheduleNextPoll() {
    this.timer = setTimeout(async () => {
      await this.poll()
      if (!this.stopped) this._scheduleNextPoll()
    }, this.pollMsValue)
  }

  async poll() {
    try {
      const response = await get(this.statusUrlValue, { responseKind: "json" })
      if (!response.ok) return
      const data = await response.json
      const info = data[this.campaignValue]
      if (info && !info.active) {
        this.stopped = true
        this._reload()
      }
    } catch {
      // transient network error — next poll retries
    }
  }

  // Seam for tests — jsdom does not allow stubbing window.location itself.
  _reload() {
    window.location.reload()
  }
}
